import { createClient } from '@/lib/supabase/client';
import type { Memo, MemoStatus } from '@/lib/database';
import { emptyToNull, newId, orThrow, DEFAULT_PAGE_SIZE } from './useEntityList';
import { setDiamondStatus } from './diamonds';

// DB column `memo_status` maps to UI field `status` — this file owns that mapping.
type MemoRow = Omit<Memo, 'status'> & { memo_status: MemoStatus };

function toMemo(row: MemoRow): Memo {
  const { memo_status, ...rest } = row;
  return { ...rest, status: memo_status };
}

function fromMemo(
  input: Omit<Memo, 'id' | 'created_at'> & { status: MemoStatus; returned_date?: string | null }
): Record<string, unknown> {
  return emptyToNull(
    {
      memo_number: input.memo_number,
      customer_id: input.customer_id,
      customer_name: input.customer_name,
      diamond_id: input.diamond_id,
      sku: input.sku,
      shape: input.shape,
      carat: input.carat,
      color: input.color,
      clarity: input.clarity,
      price_usd: input.price_usd,
      dispatched_date: input.dispatched_date || null,
      return_due_date: input.return_due_date || null,
      returned_date: input.returned_date || null,
      memo_status: input.status,
      notes: input.notes ?? '',
    },
    ['customer_id', 'diamond_id', 'sku', 'shape', 'color', 'clarity']
  );
}

export async function listMemos(): Promise<Memo[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('memos')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('dispatched_date', { ascending: false });
  return (orThrow(data, error) as MemoRow[]).map(toMemo);
}

export async function createMemo(
  input: Omit<Memo, 'id' | 'created_at'> & { returned_date?: string | null }
): Promise<Memo> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('memos')
    .insert({ ...fromMemo(input), id: newId('memo'), created_at: new Date().toISOString() })
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  const memo = toMemo(orThrow(data, error) as MemoRow);
  // Dispatching a memo takes the stone out of available inventory.
  if (memo.diamond_id) {
    await setDiamondStatus(memo.diamond_id, 'ON_MEMO').catch(() => {});
  }
  return memo;
}

export async function updateMemo(
  id: string,
  patch: Partial<Omit<Memo, 'id' | 'created_at'>>
): Promise<Memo> {
  const supabase = createClient();
  const payload: Record<string, unknown> = { ...patch };
  if (patch.status) {
    payload.memo_status = patch.status;
    delete payload.status;
  }
  if (patch.returned_date === '') payload.returned_date = null;
  const { data, error } = await supabase
    .from('memos')
    .update(payload)
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  const memo = toMemo(orThrow(data, error) as MemoRow);
  // A returned memo puts the stone back into available inventory.
  if (memo.status === 'RETURNED' && memo.diamond_id) {
    await setDiamondStatus(memo.diamond_id, 'AVAILABLE').catch(() => {});
  }
  return memo;
}
